// "Why do you think that?" — a conversation about one thing Oreia holds about you.
//
// Ported from iOS's ObservationDiscussView. The endpoint has been finished, premium-gated and
// shipping on the phone for months; the browser had never called it, which left the web's only
// answers to a wrong observation being to rewrite it yourself or make it disappear.
//
// The one piece of protocol worth knowing: the server's prompt instructs the model to prefix any
// rewording with "Revised:", and accepting one is two calls — PUT the new text, then affirm it. The
// affirm matters. A wording someone negotiated and accepted is, by definition, one that fits, and
// leaving it pending would ask them the same question again next week.

import { useEffect, useRef, useState } from 'react';
import {
  adjustObservation,
  discussObservation,
  proposedRevision,
  respondToObservation,
  type Observation,
} from '../api/portrait';
import { ApiError } from '../api/client';
import { IconClose } from '../components/Icons';
import { PrimaryButton } from '../components/ui';

interface Turn {
  role: 'you' | 'oreia';
  text: string;
}

/** What to say when the stream will not run. The gates are not failures — they have an answer. */
export function discussErrorText(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.status === 402) {
      return 'Talking a pattern through is part of the fuller Oreia. Your daily AI credits renew at midnight, or you can expand your capacity to continue now.';
    }
    if (err.status === 429) {
      return "You've reached today's limit for this. Try again tomorrow, or expand your capacity to continue.";
    }
  }
  return "I'm having trouble connecting right now. Try again in a moment.";
}

export interface DiscussSheetProps {
  observation: Observation;
  onClose(): void;
  /** The card was settled here — reworded and affirmed — so the caller can move it on. */
  onResolved(text: string): void;
}

export function DiscussSheet({ observation, onClose, onResolved }: DiscussSheetProps) {
  const [turns, setTurns] = useState<Turn[]>([]);
  const [streaming, setStreaming] = useState(false);
  const [revision, setRevision] = useState<string | null>(null);
  const [input, setInput] = useState('');
  const [accepting, setAccepting] = useState(false);

  const scroller = useRef<HTMLDivElement>(null);
  const abort = useRef<AbortController | null>(null);

  useEffect(() => () => abort.current?.abort(), []);

  // Escape closes the conversation, not whatever is behind it. Stopped rather than merely handled,
  // for the same reason as the other sheets: screens listen for Escape on `window`.
  useEffect(() => {
    function onKey(event: KeyboardEvent) {
      if (event.key !== 'Escape') return;
      event.stopPropagation();
      onClose();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Follow the stream down. Keyed on the turns themselves, so a reader who scrolled up to re-read the
  // observation is not yanked back by an idle re-render.
  useEffect(() => {
    scroller.current?.scrollTo({ top: scroller.current.scrollHeight, behavior: 'smooth' });
  }, [turns]);

  async function send(prompt: string | null) {
    if (streaming) return;

    const controller = new AbortController();
    abort.current = controller;
    const { signal } = controller;

    setTurns((t) => [
      ...t,
      ...(prompt ? [{ role: 'you' as const, text: prompt }] : []),
      { role: 'oreia' as const, text: '' },
    ]);
    setStreaming(true);
    setRevision(null);

    let accumulated = '';
    const write = (text: string) =>
      setTurns((t) => t.map((turn, i) => (i === t.length - 1 ? { ...turn, text } : turn)));

    try {
      await discussObservation(
        observation.id,
        prompt,
        (token) => {
          accumulated += token;
          write(accumulated);
        },
        signal,
      );
      if (!accumulated.trim()) write('I am still sitting with this one. Ask me again in a moment.');
    } catch (err) {
      if (signal.aborted) return;
      // Tokens that already landed are a real partial answer; only an empty turn gets replaced.
      if (!accumulated.trim()) write(discussErrorText(err));
    } finally {
      if (!signal.aborted) {
        setStreaming(false);
        setRevision(proposedRevision(accumulated));
      }
    }
  }

  // Open with the server's own framing rather than an empty room. Keyed on the observation so
  // reopening the sheet on a different card starts fresh; the sheet is unmounted between cards.
  useEffect(() => {
    void send(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- opening turn, once per observation
  }, [observation.id]);

  async function accept(text: string) {
    setAccepting(true);
    try {
      await adjustObservation(observation.id, text);
      // Best-effort: the rewrite is the part that had to land. Failing to also affirm it means the
      // card gets asked about again, which is a much smaller loss than losing the wording.
      await respondToObservation(observation.id, 'affirm').catch(() => {});
      onResolved(text);
    } catch {
      setAccepting(false);
    }
  }

  function submit(event: React.FormEvent) {
    event.preventDefault();
    const text = input.trim();
    if (!text || streaming) return;
    setInput('');
    void send(text);
  }

  return (
    <>
      <div className="orset-scrim" onClick={onClose} />
      <div className="orset odiscuss" role="dialog" aria-modal="true" aria-label="Talk this through">
        <div className="orset-head">
          <h2>Talk this through</h2>
          <button className="orset-x" onClick={onClose} aria-label="Close">
            <IconClose />
          </button>
        </div>

        <blockquote className="odiscuss-subject">{observation.text}</blockquote>

        <div className="odiscuss-turns" ref={scroller} aria-live="polite" aria-busy={streaming}>
          {turns.map((turn, i) => (
            <div className={`odiscuss-turn is-${turn.role}`} key={i}>
              {turn.text ? (
                turn.text
                  .split(/\n{2,}/)
                  .filter((p) => p.trim())
                  .map((paragraph, p) => <p key={p}>{paragraph.trim()}</p>)
              ) : (
                <p className="odiscuss-waiting">Thinking about why…</p>
              )}
              {streaming && turn.text && i === turns.length - 1 ? (
                <span className="oanalysis-caret" aria-hidden />
              ) : null}
            </div>
          ))}
        </div>

        {revision ? (
          <div className="odiscuss-revision">
            <span className="odiscuss-revision-label">Oreia suggests</span>
            <p>{revision}</p>
            <PrimaryButton loading={accepting} onClick={() => void accept(revision)}>
              Use this version
            </PrimaryButton>
          </div>
        ) : null}

        <form className="odiscuss-ask" onSubmit={submit}>
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Say more…"
            aria-label="Reply to Oreia"
            disabled={streaming}
          />
          <button type="submit" disabled={streaming || !input.trim()}>
            Send
          </button>
        </form>
      </div>
    </>
  );
}
